import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';

const NeoButton = ({
    variant = 'primary',
    loading = false,
    loadingText = 'Carregando...',
    icon = null,
    iconRight = null,
    pill = false,
    circle = false,
    square = false, // Quadrado com cantos arredondados (10px) — para ícones em tabelas
    size = 'md', // 'sm' | 'md' | 'lg'
    children,
    disabled,
    className = '',
    style = {},
    ...props
}) => {
    const isIconOnly = circle || square;

    let customStyle = {
        fontFamily: 'Outfit, sans-serif',
        fontWeight: '800',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: '1',
        verticalAlign: 'middle',
        ...style
    };

    if (size === 'sm') {
        customStyle.height = '32px';
        customStyle.fontSize = '12px';
        customStyle.padding = isIconOnly ? '0' : '0 12px';
    } else if (size === 'lg') {
        customStyle.height = '48px';
        customStyle.fontSize = '15px';
        customStyle.padding = isIconOnly ? '0' : '0 24px';
    } else {
        customStyle.height = '38px';
        customStyle.fontSize = '13.5px';
        customStyle.padding = isIconOnly ? '0' : '0 18px';
    }

    if (pill) {
        customStyle.borderRadius = '30px';
    }

    if (circle) {
        const sizeDim = size === 'sm' ? '32px' : size === 'lg' ? '48px' : '38px';
        customStyle.width = sizeDim;
        customStyle.height = sizeDim;
        customStyle.borderRadius = '50%';
        customStyle.padding = '0';
        customStyle.minWidth = sizeDim;
        customStyle.flexShrink = 0;
        customStyle['--square-size'] = sizeDim;
    }

    if (square) {
        const sizeDim = size === 'sm' ? '32px' : size === 'lg' ? '48px' : '38px';
        customStyle.width = sizeDim;
        customStyle.height = sizeDim;
        customStyle.padding = '0';
        customStyle.minWidth = sizeDim;
        customStyle.flexShrink = 0;
        customStyle['--square-size'] = sizeDim;
    }

    const isOutline = variant.startsWith('outline');
    const btnVariantClass = `neo-btn-${variant}`;

    // Calcula dinamicamente o tamanho do ícone para botões icon-only (evita muito espaço vazio)
    const iconSizeClass = isIconOnly
        ? (size === 'sm' ? 'font-size-18' : size === 'lg' ? 'font-size-24' : 'font-size-20')
        : 'font-size-15';

    return (
        <Button
            color={variant === 'link' ? 'link' : 'none'}
            outline={isOutline}
            disabled={loading || disabled}
            className={`neo-btn ${btnVariantClass} ${pill ? 'rounded-pill' : ''} ${circle ? 'neo-btn-circle rounded-circle' : ''} ${square ? 'neo-btn-square' : ''} ${className}`}
            style={customStyle}
            size={size === 'md' ? undefined : size}
            {...props}
        >
            {loading ? (
                <>
                    <i className={`mdi mdi-loading mdi-spin ${isIconOnly ? 'm-0' : 'me-2'} ${iconSizeClass}`}></i>
                    {!isIconOnly && loadingText}
                </>
            ) : (
                <>
                    {icon && <i className={`mdi ${icon} ${isIconOnly ? 'm-0' : 'me-1.5'} ${iconSizeClass}`}></i>}
                    {!isIconOnly && children}
                    {iconRight && !isIconOnly && <i className={`mdi ${iconRight} ms-1.5 ${iconSizeClass}`}></i>}
                </>
            )}
        </Button>
    );
};

NeoButton.propTypes = {
    variant: PropTypes.oneOf([
        'primary', 'chocolate', 'secondary', 'light',
        'outline-primary', 'outline-chocolate', 'outline-secondary',
        'outline-success', 'outline-danger', 'outline-warning', 'outline-info', 'outline-dark',
        'danger', 'success', 'warning', 'info', 'dark',
        'soft-primary', 'soft-chocolate', 'soft-secondary',
        'soft-success', 'soft-danger', 'soft-warning', 'soft-info', 'soft-dark',
        'glass-light', 'glass-dark', 'link'
    ]),
    loading: PropTypes.bool,
    loadingText: PropTypes.string,
    icon: PropTypes.string,
    iconRight: PropTypes.string,
    pill: PropTypes.bool,
    circle: PropTypes.bool,
    square: PropTypes.bool,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    style: PropTypes.object,
    children: PropTypes.node,
    size: PropTypes.oneOf(['sm', 'md', 'lg'])
};

export default NeoButton;
